import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, PackageMinus, RefreshCw, Save } from "lucide-react";
import PageContainer from "../../components/PageContainer";
import ProductAutocomplete from "../../components/ProductAutocomplete";
import HistoricoSaidas from "../../components/HistoricoSaidas";
import { saidaEstoqueService } from "../../services/apiService";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { useNotification } from "../../hooks/useNotification";
import CadastroRapidoModal from "../../components/estoque/CadastroRapidoModal";
import {
  Button,
  EmptyState,
  LoadingState,
  SectionCard,
} from "../../components/ui";

const uuid = () =>
  crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const nomes = {
  CONSUMO: "Consumo",
  PERDA: "Perda",
  AVARIA: "Avaria",
};

const mensagensSucesso = {
  CONSUMO: "Consumo registrado",
  PERDA: "Perda registrada",
  AVARIA: "Avaria registrada",
};

export default function SaidasEstoque() {
  const [aba, setAba] = useState("nova");
  const [tipo, setTipo] = useState("CONSUMO");
  const [produtoId, setProdutoId] = useState("");
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [quantidade, setQuantidade] = useState(1);
  const [observacao, setObservacao] = useState("");
  const [buscaProduto, setBuscaProduto] = useState("");
  const [filtro, setFiltro] = useState("TODOS");
  const [loading, setLoading] = useState(false);
  const [cadastroRapido, setCadastroRapido] = useState(false);
  const { showSuccess, showError } = useNotification();
  const confirmar = useConfirmDialog();
  const client = useQueryClient();
  const perda = tipo !== "CONSUMO";

  const dados = useQuery({
    queryKey: ["saidas-estoque", "historico"],
    queryFn: saidaEstoqueService.historico,
  });
  const produtosQuery = useQuery({
    queryKey: ["saidas-estoque", "produtos", buscaProduto],
    queryFn: () => saidaEstoqueService.produtos(buscaProduto),
    enabled: buscaProduto.trim().length >= 3,
    staleTime: 10000,
    retry: 0,
  });
  const saldoQuery = useQuery({
    queryKey: ["saidas-estoque", "saldo", produtoId],
    queryFn: () => saidaEstoqueService.saldo(produtoId),
    enabled: Boolean(produtoId),
    retry: 0,
  });

  const saldo = saldoQuery.data;
  const produtos = produtosQuery.data || [];
  const historico = useMemo(() => dados.data || [], [dados.data]);
  const opcoes = produtos.map((produto) => ({
    value: produto.produtoId,
    label: `${produto.codigo || "Sem código"} — ${produto.descricao} (${produto.saldoAtual} ${produto.unidadeSimbolo || ""})`,
  }));
  const visiveis = useMemo(
    () =>
      historico.filter(
        (movimento) =>
          filtro === "TODOS" ||
          (filtro === "CONSUMO"
            ? movimento.tipo === "SAIDA_CONSUMO"
            : movimento.tipo === filtro),
      ),
    [historico, filtro],
  );
  const bloqueado =
    loading ||
    !saldo ||
    Number(quantidade) > Number(saldo?.saldoAtual || 0) ||
    (perda && !observacao.trim());

  const salvar = async (evento) => {
    evento.preventDefault();
    if (!saldo) return;
    if (perda && !observacao.trim()) {
      showError("Informe o motivo da perda ou avaria.");
      return;
    }

    const nome = nomes[tipo].toLowerCase();
    const confirmado = await confirmar({
      title: `Confirmar ${nome}`,
      description: `Registrar ${quantidade} ${saldo.unidadeSimbolo || "unidade(s)"} como ${nome}? O saldo será reduzido.`,
      variant: perda ? "warning" : "success",
    });
    if (!confirmado) return;

    setLoading(true);
    try {
      await saidaEstoqueService.registrar({
        produtoId,
        tipo,
        quantidade: Number(quantidade),
        observacao,
        expectedVersion: saldo.versaoSaldo,
        requestId: uuid(),
      });
      showSuccess(`${mensagensSucesso[tipo]} com sucesso.`);
      setProdutoId("");
      setProdutoSelecionado(null);
      setQuantidade(1);
      setObservacao("");
      await client.invalidateQueries({ queryKey: ["saidas-estoque"] });
    } catch (erro) {
      showError(erro.message);
    } finally {
      setLoading(false);
    }
  };

  const selecionarTipo = (evento) => {
    setTipo(evento.target.value);
    setProdutoId("");
    setProdutoSelecionado(null);
    setObservacao("");
  };

  const selecionarProduto = (id) => {
    setProdutoId(id);
    setProdutoSelecionado(null);
  };

  const produtoCriado = (produto) => {
    const opcao = {
      value: produto.id,
      label: `${produto.codigo || "Sem código"} — ${produto.descricao}`,
    };
    setProdutoSelecionado(opcao);
    setProdutoId(produto.id);
    setBuscaProduto(produto.descricao || "");
    client.invalidateQueries({ queryKey: ["saidas-estoque", "produtos"] });
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Saídas de estoque</h1>
        <p className="text-gray-600">Registre consumo, perdas ou avarias.</p>
      </div>
      <div className="mb-5 inline-flex max-w-full flex-wrap gap-2 rounded-xl border border-gray-200 bg-white p-2">
        <button
          type="button"
          onClick={() => setAba("nova")}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${aba === "nova" ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-blue-50"}`}
        >
          <PackageMinus size={17} />
          Nova saída
        </button>
        <button
          type="button"
          onClick={() => setAba("historico")}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${aba === "historico" ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-blue-50"}`}
        >
          <ClipboardList size={17} />
          Histórico
        </button>
      </div>
      {(dados.isError || produtosQuery.isError) && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-red-700">
          {(produtosQuery.error || dados.error).message}
        </div>
      )}
      {aba === "nova" && (
        <>
          <form
            onSubmit={salvar}
            className="mb-8 rounded-lg border bg-white p-5 shadow-sm"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <label>
                Tipo de saída *
                <select
                  value={tipo}
                  onChange={selecionarTipo}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="CONSUMO">Consumo</option>
                  <option value="PERDA">Perda</option>
                  <option value="AVARIA">Avaria</option>
                </select>
              </label>
              <label>
                Quantidade *
                <input
                  required
                  type="number"
                  min="1"
                  value={quantidade}
                  onChange={(evento) => setQuantidade(evento.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="md:col-span-2">
                Produto *
                <ProductAutocomplete
                  value={produtoId}
                  onChange={selecionarProduto}
                  selectedOption={produtoSelecionado}
                  onSearch={setBuscaProduto}
                  searchLoading={produtosQuery.isFetching}
                  placeholder="Pesquise por código ou descrição"
                  options={opcoes}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-2"
                  onClick={() => setCadastroRapido(true)}
                >
                  Novo produto
                </Button>
              </label>
              <label className="md:col-span-2">
                {perda ? "Motivo *" : "Observação (opcional)"}
                <textarea
                  required={perda}
                  maxLength="1000"
                  value={observacao}
                  onChange={(evento) => setObservacao(evento.target.value)}
                  className="mt-1 min-h-24 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
            </div>
            {saldoQuery.isError && (
              <p className="mt-3 text-sm text-red-600">
                {saldoQuery.error.message}
              </p>
            )}
            {saldo && (
              <div className="mt-4 rounded-lg bg-gray-50 p-3 text-sm">
                Saldo disponível:{" "}
                <b>
                  {saldo.saldoAtual} {saldo.unidadeSimbolo}
                </b>
              </div>
            )}
            <button
              disabled={bloqueado}
              className="mt-5 flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:bg-gray-400"
            >
              <Save size={17} />
              Registrar saída
            </button>
          </form>
          <CadastroRapidoModal
            open={cadastroRapido}
            tipo="produto"
            onClose={() => setCadastroRapido(false)}
            onCreated={produtoCriado}
          />
        </>
      )}
      {aba === "historico" && (
        <section>
          <SectionCard className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <ClipboardList size={19} />
                  Histórico de saídas
                </h2>
                <p className="text-sm text-gray-600">
                  Consulte as movimentações já registradas.
                </p>
              </div>
              <Button
                variant="ghost"
                icon={RefreshCw}
                onClick={() =>
                  Promise.all([dados.refetch(), produtosQuery.refetch()])
                }
                disabled={dados.isFetching || produtosQuery.isFetching}
              >
                Atualizar
              </Button>
            </div>
            <div className="mt-4">
              <label className="block max-w-xs text-sm font-medium text-gray-700">
                Tipo de saída
                <select
                  value={filtro}
                  onChange={(evento) => setFiltro(evento.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="TODOS">Todos</option>
                  <option value="CONSUMO">Consumo</option>
                  <option value="PERDA">Perdas</option>
                  <option value="AVARIA">Avarias</option>
                </select>
              </label>
            </div>
          </SectionCard>
          {dados.isLoading ? (
            <LoadingState message="Carregando histórico..." />
          ) : !visiveis.length ? (
            <EmptyState
              title="Nenhuma saída encontrada"
              description="Ajuste o filtro ou registre uma nova saída."
            />
          ) : (
            <SectionCard bodyClassName="p-0">
              <HistoricoSaidas historico={visiveis} />
            </SectionCard>
          )}
        </section>
      )}
    </PageContainer>
  );
}
